import { CommonTooltips } from "@mcleod/common";
import {
    ClickEvent, Component, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, EditRowDecorator, ForcedCase,
    Label, Layout, LookupModelSelectionEvent, Panel, TableRow, TableRowDisplayEvent
} from "@mcleod/components";
import { Alignment, CurrencyUtil, DisplayType, Keys, ModelRow, Navigation, StringUtil, UrlUtil } from "@mcleod/core";
import { OrderTabView } from "@mcleod/dispatch";
import { EdiMultiStop } from "./EdiMultiStop";
import { AutogenLayoutTenderList } from "./autogen/AutogenLayoutTenderList";

export class TenderList extends AutogenLayoutTenderList {
    override onLoad() {

        this.citystateEdiStopOriginCityId.dataSourceModeChanged(DataSourceMode.SEARCH);
        this.citystateEdiStopDestCityId.dataSourceModeChanged(DataSourceMode.SEARCH);
        this.citystateEdiStopOriginCityId.textCity.forcedCase = ForcedCase.UPPER;
        this.citystateEdiStopDestCityId.textCity.forcedCase = ForcedCase.UPPER;
        this.checkboxEdiOrderNoDisplay.allowIndeterminate = true;
        this.checkboxEdiOrderNoDisplay.checked = undefined;
        this.switchIntercompany.allowIndeterminate = true;
        this.switchIntercompany.checked = undefined;
        this.switchReplyTransmitted.allowIndeterminate = true;
        this.switchReplyTransmitted.checked = undefined;

        const urlProp = UrlUtil.getPropsFromUrl(window.location.search);
        if (urlProp != null && urlProp.id != null)
            this.mainDataSource.search({ order_id: urlProp.id });

        this.textboxOriginLocation.addLookupModelSelectionListener((event: LookupModelSelectionEvent) =>
            this.searchStopPopulateSelection("origin", this.panelShipperLocation, event));
        this.textboxDestLocation.addLookupModelSelectionListener(event =>
            this.searchStopPopulateSelection("consignee", this.panelConsigneeLocation, event)
        );
    }

    searchStopPopulateSelection(stopType: "origin" | "consignee", targetPanel: Panel, event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selection = event.selection;
            this.sourceEdiOrdersList.searchRow.setValues({
                [`stop_${stopType}.address`]: selection.getString("address1", null),
                [`stop_${stopType}.address2`]: selection.getString("address2", null),
                [`stop_${stopType}.city_id`]: selection.getString("city_id", null),
                [`stop_${stopType}.city_name`]: selection.getString("city_name", null),
                [`stop_${stopType}.location_id`]: selection.getString("id", null),
                [`stop_${stopType}.location_name`]: selection.getString("name", null),
                [`stop_${stopType}.state`]: selection.getString("state", null),
                [`stop_${stopType}.zip_code`]: selection.getString("zip_code", null),
            });
        }
        this.searchStopSelection(stopType, targetPanel, event);
    }

    searchStopSelection(stopType: "origin" | "consignee", targetPanel: Panel, event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selection = event.selection;
            if (!selection.isNull("state")) {
                this.sourceEdiOrdersList.searchRow.setLookupModelData(`edi_stop_${stopType}.state`, { "id": selection.getString("state", null) });
            }
        }
        (targetPanel as TenderList).displayComponentData(this.sourceEdiOrdersList.searchRow, null, 0);
    }

    tableTendersOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = event.getTableRow().data as ModelRow;
        const labelOrderId = tableRow.findComponentById("labelOrderId") as Label;
        const labelPartnerName = tableRow.findComponentById("labelPartnerName") as Label;
        const labelStops = tableRow.findComponentById("labelStops") as Label;
        const labelTotalCharges = tableRow.findComponentById("labelTotalCharges") as Label;
        const labelShipmentId = tableRow.findComponentById("labelShipmentId") as Label;
        const labelBOL = tableRow.findComponentById("labelBOL") as Label;
        const labelReplyTransmitted = tableRow.findComponentById("labelReplyTransmitted") as Label;
        const TRANSMITTED = "4";
        const SUCCESSFUL = "Y";
        const RECOVERED = "R";
        if (modelRow.get("order_id")) labelOrderId.addUnderlineWhenMouseOver();

        labelStops.applyLinkStyle();
        this.setTooltipContent(labelPartnerName, modelRow);

        this.setTooltipContent(labelTotalCharges, modelRow);
        if (modelRow.get("direction") === "O" && modelRow.get("carrier_id") != undefined) {
            if (modelRow.get("total_pay") == undefined) {
                labelTotalCharges.text = "$0.00";
            } else {
                labelTotalCharges.text = CurrencyUtil.formatCurrency(modelRow.get("total_pay"));
            }

        }

        if (labelShipmentId?.text?.length > 0 || labelBOL?.text?.length > 0) {
            this.setTooltipContent(labelShipmentId, modelRow);
            this.setTooltipContent(labelBOL, modelRow);
        }

        this.setTooltipContent(labelReplyTransmitted, modelRow);
        if (modelRow.get("direction") === "I") {
            labelReplyTransmitted.text = (modelRow.get("edi_log_reply.status") === TRANSMITTED)
                                            && (modelRow.get("edi_log_reply.was_successful") === SUCCESSFUL
                                                || modelRow.get("edi_log_reply.was_successful") === RECOVERED) ? "Yes" : "No";
        } else if (modelRow.get("direction") === "O") {
            labelReplyTransmitted.text = (modelRow.get("edi_log_tenders.status") === TRANSMITTED)
                                            && (modelRow.get("edi_log_tenders.was_successful") === SUCCESSFUL
                                                || modelRow.get("edi_log_tenders.was_successful") === RECOVERED) ? "Yes" : "No";
        }
    }

    setTooltipContent(component: Component, modelRow: ModelRow) {
        if (component.id == null) return;
        switch (component.id) {
            case "labelPartnerName":
                CommonTooltips.setTooltipContentLayout(component, null, "lme/datafusion/EdiProfileOrderQuickInfo", null,
                    {
                        "direction": modelRow.get("direction"),
                        "partner_id": modelRow.get("partner_id"),
                        "alt_partner_id": modelRow.get("alt_partner_id"),
                        "version": modelRow.get("version"),
                        "batch": modelRow.get("gs06_group_cntlno"),
                        "create_date": modelRow.get("create_date"),
                        "trans_type": 'T'
                    }
                );
                component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
                return;
            case "labelTotalCharges":
                const labelTotalCharges = component as Label;
                const rateTypeMethod = modelRow.get("rate_type_descr");
                let rate = modelRow.get("rate");
                let units = modelRow.get("rate_units");
                let charge = modelRow.get("freight_charge");
                let rateId = modelRow.get("rate_id");
                let otherCharge = modelRow.get("otherchargetotal");
                let chargeCaption = "Freight Charge";
                let otherChargeCaption = "Other Charges";

                if (modelRow.get("direction") === "O"
                    && modelRow.get("carrier_id") != undefined) {
                    rate = modelRow.get("override_pay_rate");
                    units = modelRow.get("override_pay_units");
                    charge = modelRow.get("override_pay_amt");
                    rateId = "";
                    otherCharge = modelRow.get("other_pay_total");
                    chargeCaption = "Carrier Pay";
                    otherChargeCaption = "Pay Adjustments";
                }

                return CommonTooltips.setTooltipContentTextboxes(component, () => [
                    {
                        caption: "Type",
                        displayType: DisplayType.STRING,
                        text: rateTypeMethod,
                        rowBreak: false,
                        fillRow: true
                    },
                    { caption: "Rate", displayType: DisplayType.FLOAT, text: rate, marginLeft: 55 },
                    { caption: "Units", displayType: DisplayType.STRING, text: units, rowBreak: false, fillRow: true },
                    {
                        caption: chargeCaption,
                        displayType: DisplayType.FLOAT,
                        text: CurrencyUtil.formatCurrency(charge),
                        marginLeft: 20
                    },
                    {
                        caption: "Rate ID",
                        displayType: DisplayType.STRING,
                        text: rateId,
                        rowBreak: false,
                        fillRow: true
                    },
                    {
                        caption: otherChargeCaption,
                        displayType: DisplayType.FLOAT,
                        text: CurrencyUtil.formatCurrency(otherCharge),
                        marginLeft: 10
                    }
                ]);

            case "labelShipmentId":
            case "labelBOL":
                CommonTooltips.setTooltipContentTextboxes(component, () => [
                    { caption: "Shipment ID", displayType: DisplayType.STRING, text: modelRow.get("shipment_id") },
                    { caption: "Bill of Lading", displayType: DisplayType.STRING, text: modelRow.get("blnum") }
                ]);
                component.tooltipDynamicOptions = { position: Alignment.LEFT };
                return;
            case "labelReplyTransmitted":
                let batch = modelRow.get("gs06_group_cntlno");
                let transType = "T";

                if (modelRow.get("direction") === "I") {
                    batch = modelRow.get("reply_batch_id");
                    transType = "R";
                }

                CommonTooltips.setTooltipContentLayout(component, null, "lme/datafusion/TransmissionStatusQuickInfo", null, {
                        "direction": "O",
                        "func_batch": batch,
                        "func_receiver_id": modelRow.get("partner_id"),
                        "receiver_id": modelRow.get("alt_partner_id"),
                        "func_version": modelRow.get("version"),
                        "transaction_type": transType
                    }
                );
                component.tooltipDynamicOptions = { position: Alignment.LEFT };
                return;
        }
    }

    labelStopsOnClick(event: ClickEvent) {
        const label = event.target as Label;
        label.busy = true;
        const tableRow: TableRow = TableRow.getContainingTableRow(label);
        tableRow.table.selectedRow = tableRow;
        const row = tableRow.data;
        let ediMultiStopLayout: EdiMultiStop;
        const erd = new EditRowDecorator({
            title: `Stop Details - Tender ${row.get("id")}`,
            layout: ediMultiStopLayout = Layout.getLayout("lme/datafusion/EdiMultiStop") as EdiMultiStop,
            layoutLoadListeners: async (event) => {
                await ediMultiStopLayout.mainDataSource.search({ id: row.get("id") });
                ediMultiStopLayout.mainDataSource.mode = DataSourceMode.NONE;
            },
            width: '70%',
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            onClose: (cancelled: boolean) => ediMultiStopLayout.slideOut(),
            doAfterSlideIn: () => label.busy = false
        });
    }

    orderNumberOnClick(event: ClickEvent) {
        if ((event.target as Label).text) {
            OrderTabView.navigateTo({ key: (event.target as Label).text, newTab: true });
        }
    }
}
